import React, { createContext, useState, useCallback, ReactNode } from "react";
import api from "../../api/api";
import { PaginationInfo } from "../../types/api";
import { DailyLabourLog, CreateDailyLabourLogData, UpdateDailyLabourLogData } from "../../types/models";
import { DailyLabourLogContextType } from "../../types/context";

export const DailyLabourLogContext = createContext<DailyLabourLogContextType | undefined>(undefined);

interface DailyLabourLogProviderProps {
  children: ReactNode;
}

export const DailyLabourLogProvider: React.FC<DailyLabourLogProviderProps> = ({ children }) => {
  const [dailyLabourLogs, setDailyLabourLogs] = useState<DailyLabourLog[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDailyLabourLogs = useCallback(async (
    projectId: number | null = null,
    labourId: number | null = null,
    workDate: string | null = null,
    page: number = 1,
    perPage: number = 15
  ): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      let url = '/daily-labour-logs';
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      if (projectId) params.append('project_id', projectId.toString());
      if (labourId) params.append('labour_id', labourId.toString());
      if (workDate) params.append('work_date', workDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let logsData: DailyLabourLog[] = [];
      let paginationData: PaginationInfo | null = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          logsData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          logsData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          logsData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        logsData = responseData.data;
      } else if (Array.isArray(responseData)) {
        logsData = responseData;
      }
      
      setDailyLabourLogs(logsData);
      setPagination(paginationData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message);
      console.error("Failed to fetch daily labour logs:", err);
      setDailyLabourLogs([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const createDailyLabourLog = useCallback(async (logData: CreateDailyLabourLogData): Promise<DailyLabourLog> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/daily-labour-logs', logData);
      const newLog = response.data.data;
      setDailyLabourLogs((prev) => [...prev, newLog]);
      return newLog;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateDailyLabourLog = useCallback(async (id: number, logData: UpdateDailyLabourLogData): Promise<DailyLabourLog> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.put(`/daily-labour-logs/${id}`, logData);
      const updatedLog = response.data.data;
      setDailyLabourLogs((prev) =>
        prev.map((log) => (log.id === id ? updatedLog : log))
      );
      return updatedLog;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteDailyLabourLog = useCallback(async (id: number): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await api.delete(`/daily-labour-logs/${id}`);
      setDailyLabourLogs((prev) => prev.filter((log) => log.id !== id));
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getDailyLabourLog = useCallback(async (id: number): Promise<DailyLabourLog | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/daily-labour-logs/${id}`);
      return response.data.data;
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getProjectLabourLogs = useCallback(async (projectId: number): Promise<DailyLabourLog[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/projects/${projectId}/labour-logs`);
      return response.data.data?.data || response.data.data || [];
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getLabourLogs = useCallback(async (labourId: number): Promise<DailyLabourLog[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/labours/${labourId}/logs`);
      return response.data.data?.data || response.data.data || [];
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message;
      setError(errorMsg);
      throw err.response?.data || err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value: DailyLabourLogContextType = {
    dailyLabourLogs,
    pagination: pagination || undefined,
    loading,
    error,
    fetchDailyLabourLogs,
    createDailyLabourLog,
    updateDailyLabourLog,
    deleteDailyLabourLog,
    getDailyLabourLog,
    getProjectLabourLogs,
    getLabourLogs,
  };

  return (
    <DailyLabourLogContext.Provider value={value}>
      {children}
    </DailyLabourLogContext.Provider>
  );
};

export default DailyLabourLogProvider;

